import {
  Box,
  Typography,
  Avatar,
  useTheme,
  alpha,
  Chip,
} from "@mui/material";
import { STATUS_META } from "./constants";
import BookingModal from "./BookingModal";

export default function BookingCard({
  booking,
  leadName,
  type,
  open,
  onToggle,
  onUpdate,
  onDelete,
  globalBookedSlots,
  durations = [],
  workingHours,
}) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  // Colors consistent with Reports_v2
  const colors = {
    success: "#10B981",
    successLight: "#ECFDF5",
    warning: "#F59E0B",
    warningLight: "#FFFBEB",
    danger: "#EF4444",
    dangerLight: "#FEF2F2",
    bgCard: "#FFFFFF",
    bgHover: "#F8FAFC",
    textPrimary: "#0F172A",
    textSecondary: "#64748B",
    textMuted: "#94A3B8",
    border: "#E2E8F0",
  };

  const status = booking?.status || "Scheduled";
  const StatusIcon = STATUS_META[status]?.icon || STATUS_META.Scheduled.icon;
  const statusColor = {
    Scheduled: primaryColor,
    Confirmed: colors.success,
    Ongoing: colors.warning,
    Completed: colors.success,
    Cancelled: colors.danger,
  }[status] || primaryColor;

  // Format display date
  const displayDate = booking?.date
    ? new Date(booking.date).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      })
    : "No date";

  // Get consultant name
  const consultantName = booking?.consultant?.name || booking?.consultant_name || null;

  // Capitalize first letter of status
  const displayStatus = status.charAt(0).toUpperCase() + status.slice(1);

  return (
    <>
      {/* Booking Item Card */}
      <Box
        onClick={onToggle}
        sx={{
          p: 2,
          px: 2.5,
          borderRadius: 3,
          bgcolor: colors.bgCard,
          boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04)",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 2,
          transition: "all 0.2s ease",
          border: `1px solid ${colors.border}`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            top: 0,
            bottom: 0,
            width: 4,
            bgcolor: statusColor,
            opacity: 0,
            transition: "opacity 0.2s ease",
          },
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)",
            borderColor: alpha(statusColor, 0.3),
            bgcolor: colors.bgHover,
            "&::before": {
              opacity: 1,
            },
          },
        }}
      >
        {/* Status Icon */}
        <Avatar
          sx={{
            bgcolor: alpha(statusColor, 0.12),
            color: statusColor,
            width: 42,
            height: 42,
            borderRadius: 2.5,
            boxShadow: `0 2px 8px ${alpha(statusColor, 0.15)}`,
          }}
        >
          <StatusIcon sx={{ fontSize: 20 }} />
        </Avatar>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: 14,
                color: colors.textPrimary,
                letterSpacing: -0.2,
              }}
            >
              {displayDate}
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: statusColor,
              }}
            >
              • {booking?.slot || "No Slot"}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            {booking?.duration && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: colors.textMuted,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: 0.3,
                }}
              >
                {booking.duration} Min
              </Typography>
            )}
            {consultantName && (
              <>
                <Typography sx={{ fontSize: 12, color: colors.border }}>•</Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: colors.textSecondary,
                    fontWeight: 500,
                  }}
                >
                  {consultantName}
                </Typography>
              </>
            )}
            {booking?.internal_comments && (
              <>
                <Typography sx={{ fontSize: 12, color: colors.border }}>•</Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: colors.textMuted,
                    fontStyle: "italic",
                  }}
                >
                  Has Notes
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Status Badge */}
        <Chip
          label={displayStatus}
          sx={{
            height: 26,
            fontSize: 11,
            fontWeight: 700,
            borderRadius: 2,
            bgcolor: alpha(statusColor, 0.12),
            color: statusColor,
            textTransform: "capitalize",
            "& .MuiChip-label": {
              px: 1.5,
            },
          }}
        />
      </Box>

      {/* Edit/Add Modal */}
      <BookingModal
        open={open}
        onClose={onToggle}
        onSave={onUpdate}
        onDelete={onDelete}
        booking={booking}
        leadName={leadName}
        type={type}
        durations={durations}
        workingHours={workingHours}
        globalBookedSlots={globalBookedSlots}
      />
    </>
  );
}
